import React, { useState, useEffect, forwardRef, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import classes from './CalendarPicker.module.scss';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import { Row, Col } from 'react-bootstrap';
import Button from '@/components/Button/Button';
import t from '@/utils/Language/UI-translations';
import { calendarHelpers } from '@/utils/helpers';
import moment from 'moment';

const defaultValues = {
  day: '',
  month: '',
  year: ''
};

const CalendarPickerButton = forwardRef((props, ref) => {
  return (
    <Button
      type="primary"
      text={t('Pick date')}
      icon={{ style: 'fas', font: 'calendar-alt' }}
      additionalClasses={`${classes.datePickerBtn}`}
      onButtonClick={props.onClick}
      styles={{ width: 'full' }} // new
    />
  );
});

CalendarPickerButton.displayName = 'CalendarPickerButton';

// expect selectedDate to be in the string format YYYY-MM-DD
const CalendarPicker = ({
  calendarLabel,
  selectedDate = false,
  onDateChange,
  showMonthDropDown,
  maximumDate,
  setDatePickerIsOpen,
  showYearDropDown,
  dropDownMode,
  showError = true,
  inlinePicker = false,
  ...props
}) => {
  const ref = useRef();
  const [formData, setFormData] = useState(defaultValues);
  const [errorObject, setErrorObject] = useState({ field: null, error: null, message: null });

  useEffect(() => {
    if (selectedDate) {
      setFormData(calendarHelpers.dateToParts(selectedDate));
    } else {
      setFormData(defaultValues);
    }
  }, [selectedDate]);

  const handleSelectionChange = (dateObj) => {
    if (typeof onDateChange === 'function') {
      if (!dateObj.day && !dateObj.month && !dateObj.year) {
        setErrorObject({});
        onDateChange(null);
        return;
      }
      let dateString = false;
      let validation = calendarHelpers.validateCalendar(dateObj);
      if (!validation.error) {
        let momentTime = calendarHelpers.momentFromParts(dateObj);
        dateString = momentTime.format('YYYY-MM-DD');
        setErrorObject({});
      } else {
        setErrorObject(validation);
      }
      //pass selected date to the callback in the same format as selectedDate (YYYY-MM-DD)
      onDateChange(dateString);
    }
  };

  const handlePickerChange = (dateTime) => {
    handleSelectionChange(calendarHelpers.dateToParts(dateTime));
  };

  const handleFieldChange = (e) => {
    setFormData((prevState) => {
      const updatedState = { ...prevState, [e.target.name]: e.target.value };
      // convert date parts to date object - use moment to properly account for timezones
      handleSelectionChange(updatedState);
      return updatedState;
    });
  };

  return (
    <div className={classes.dob}>
      <Row>
        <Form.Label>{calendarLabel ? calendarLabel : 'Date'}</Form.Label>
        <Col xs="4" md={inlinePicker ? '3' : '4'} className={`${classes.inputField} mb-2 pe-1`}>
          <FloatingLabel controlId="formMonthLabel" label={'MM'}>
            <Form.Control
              minLength="1"
              maxLength="2"
              type="input"
              name="month"
              placeholder={'Month'}
              value={formData['month']}
              className={`${classes.calenderInput} ${classes.monthInput} ${errorObject?.field === 'month' ? classes.calenderInputError : ''}`}
              onChange={handleFieldChange}
              autoComplete="off"
              data-cy="modal-client-input-dob-mm"
            />
          </FloatingLabel>
        </Col>

        <Col xs="4" md={inlinePicker ? '3' : '4'} className={`${classes.inputField} mb-2 ps-1 pe-1`}>
          <FloatingLabel controlId="formMonthLabel" label={'DD'}>
            <Form.Control
              minLength="1"
              maxLength="2"
              type="input"
              name="day"
              placeholder={'Day'}
              value={formData['day']}
              className={`${classes.calenderInput} ${classes.dayInput} ${errorObject?.field === 'day' ? classes.calenderInputError : ''}`}
              onChange={handleFieldChange}
              autoComplete="off"
              data-cy="modal-client-input-dob-dd"
            />
          </FloatingLabel>
        </Col>

        <Col xs="4" md={inlinePicker ? '3' : '4'} className={`${classes.inputField} mb-2 ps-1`}>
          <FloatingLabel controlId="formMonthLabel" label={'YYYY'}>
            <Form.Control
              minLength="4"
              maxLength="4"
              type="input"
              name="year"
              placeholder={'Year'}
              value={formData['year']}
              className={`${classes.calenderInput} ${classes.yearInput} ${errorObject?.field === 'year' ? classes.calenderInputError : ''}`}
              onChange={handleFieldChange}
              autoComplete="off"
              data-cy="modal-client-input-dob-yyyy"
            />
          </FloatingLabel>
        </Col>

        <Col xs="12" md={inlinePicker ? '3' : '12'} className={inlinePicker ? 'mt-2 mt-md-0 ps-md-1' : 'mt-2'}>
          <DatePicker
            selected={selectedDate ? moment(selectedDate).toDate() : false}
            onChange={handlePickerChange}
            showMonthDropdown={showMonthDropDown ? showMonthDropDown : false}
            maxDate={maximumDate ? maximumDate : new Date()}
            onCalendarClose={() => setDatePickerIsOpen(false)}
            onCalendarOpen={() => setDatePickerIsOpen(true)}
            showYearDropdown={showYearDropDown ? showYearDropDown : false}
            dropdownMode={dropDownMode ? dropDownMode : 'select'}
            ref={ref}
            customInput={<CalendarPickerButton />}
          />
        </Col>
        {showError && errorObject?.message && <Col className={`${classes.errorMessage} mt-2`}>{errorObject?.message}</Col>}
      </Row>
    </div>
  );
};

CalendarPicker.propTypes = {};
CalendarPicker.defaultProps = {};
CalendarPicker.displayName = 'CalendarPicker';
export default CalendarPicker;
